import type { JSX } from 'react';
import {
  Node,
  mergeAttributes,
  ReactNodeViewRenderer,
  NodeViewWrapper,
  NodeViewContent,
  type JSONContent,
  type NodeViewProps,
} from '@tiptap/react';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';

/**
 * Блоки компоновки (фаза 0.3 «Красивые статьи»): колонки, раскрывающийся
 * блок (toggle) и шаги. Сериализуются в .md fenced-блоками, содержимое
 * внутри — обычный Markdown (рендерится рекурсивно):
 *
 *   ```columns 2            ```toggle Заголовок      ```steps
 *   ### Левая               содержимое               ### Открыть кассу
 *   текст                   ```                      текст
 *   ---col---                                        ### Проверить сдачу
 *   ### Правая                                       текст
 *   текст                                            ```
 *   ```
 *
 * HTML (редактор, читалка и бэк одинаково, см. markdown-html.ts):
 *   <div class="fk-columns" data-columns="2"><div class="fk-col">…</div>…</div>
 *   <details class="fk-toggle" data-toggle open><summary>…</summary><div class="fk-toggle-body">…</div></details>
 *   <ol class="fk-steps" data-steps><li class="fk-step"><div class="fk-step-title">…</div><div class="fk-step-body">…</div></li>…</ol>
 *
 * Взаимная вложенность columns/toggle/steps запрещена схемой (см.
 * INNER_CONTENT): внутри — только «простые» блоки. Иначе fenced-блок в
 * fenced-блоке ломал бы Markdown.
 */

/** Что можно класть внутрь колонки / toggle / шага. */
export const INNER_CONTENT =
  '(paragraph | heading | bulletList | orderedList | taskList | blockquote | codeBlock | image | callout | imageGallery | fkTable | fkVideo | mediaEmbed | horizontalRule | table | fkEmbed | fkButton | fkFile | fkKpi)+';

export const FkColumns = Node.create({
  name: 'fkColumns',
  group: 'block',
  content: 'fkColumn{1,4}',
  defining: true,
  isolating: true,

  parseHTML() {
    return [{ tag: 'div[data-columns]' }];
  },

  renderHTML({ HTMLAttributes, node }) {
    return [
      'div',
      mergeAttributes(HTMLAttributes, { class: 'fk-columns', 'data-columns': String(node.childCount) }),
      0,
    ];
  },

  addNodeView() {
    return ReactNodeViewRenderer(ColumnsView);
  },
});

export const FkColumn = Node.create({
  name: 'fkColumn',
  content: INNER_CONTENT,
  defining: true,
  isolating: true,

  parseHTML() {
    return [{ tag: 'div.fk-col' }];
  },

  renderHTML({ HTMLAttributes }) {
    return ['div', mergeAttributes(HTMLAttributes, { class: 'fk-col' }), 0];
  },
});

function ColumnsView(props: NodeViewProps): JSX.Element {
  const { node, editor, getPos, deleteNode } = props;
  const count = node.childCount;
  const addColumn = (): void => {
    if (count >= 4) return;
    const pos = getPos();
    // Конец содержимого узла = pos + nodeSize - 1 (перед закрывающим токеном).
    editor
      .chain()
      .focus()
      .insertContentAt(pos + node.nodeSize - 1, { type: 'fkColumn', content: [{ type: 'paragraph' }] })
      .run();
  };
  const removeLast = (): void => {
    if (count <= 1) return;
    const pos = getPos();
    const last = node.child(count - 1);
    const from = pos + node.nodeSize - 1 - last.nodeSize;
    editor.chain().focus().deleteRange({ from, to: from + last.nodeSize }).run();
  };
  return (
    <NodeViewWrapper className="fk-layout-node">
      <div className="fk-layout-bar" contentEditable={false}>
        <span>Колонки: {count}</span>
        <button type="button" onClick={addColumn} disabled={count >= 4} title="Добавить колонку"><PlusOutlined /></button>
        <button type="button" onClick={removeLast} disabled={count <= 1} title="Убрать последнюю колонку">−</button>
        <button type="button" onClick={() => deleteNode()} title="Удалить блок" className="danger"><DeleteOutlined /></button>
      </div>
      <NodeViewContent className="fk-columns" data-columns={count} />
    </NodeViewWrapper>
  );
}

export const FkToggle = Node.create({
  name: 'fkToggle',
  group: 'block',
  content: INNER_CONTENT,
  defining: true,
  isolating: true,

  addAttributes() {
    return {
      title: {
        default: 'Заголовок',
        parseHTML: (el: HTMLElement) => el.querySelector('summary')?.textContent?.trim() || 'Заголовок',
        renderHTML: () => ({}),
      },
    };
  },

  parseHTML() {
    return [{ tag: 'details[data-toggle]', contentElement: '.fk-toggle-body' }];
  },

  renderHTML({ HTMLAttributes, node }) {
    return [
      'details',
      mergeAttributes(HTMLAttributes, { class: 'fk-toggle', 'data-toggle': '', open: 'true' }),
      ['summary', {}, String(node.attrs.title || 'Заголовок')],
      ['div', { class: 'fk-toggle-body' }, 0],
    ];
  },

  addNodeView() {
    return ReactNodeViewRenderer(ToggleView);
  },
});

function ToggleView(props: NodeViewProps): JSX.Element {
  const { node, updateAttributes, deleteNode } = props;
  return (
    <NodeViewWrapper className="fk-layout-node">
      <div className="fk-toggle fk-toggle-edit">
        <div className="fk-toggle-head" contentEditable={false}>
          <span className="fk-toggle-chevron">▸</span>
          <input
            value={String(node.attrs.title ?? '')}
            onChange={(e) => updateAttributes({ title: e.target.value })}
            placeholder="Заголовок раскрывающегося блока"
            className="fk-layout-input"
          />
          <button type="button" onClick={() => deleteNode()} title="Удалить блок" className="fk-layout-btn danger"><DeleteOutlined /></button>
        </div>
        <NodeViewContent className="fk-toggle-body" />
      </div>
    </NodeViewWrapper>
  );
}

export const FkSteps = Node.create({
  name: 'fkSteps',
  group: 'block',
  content: 'fkStep+',
  defining: true,
  isolating: true,

  parseHTML() {
    return [{ tag: 'ol[data-steps]' }];
  },

  renderHTML({ HTMLAttributes }) {
    return ['ol', mergeAttributes(HTMLAttributes, { class: 'fk-steps', 'data-steps': '' }), 0];
  },

  addNodeView() {
    return ReactNodeViewRenderer(StepsView);
  },
});

export const FkStep = Node.create({
  name: 'fkStep',
  content: INNER_CONTENT,
  defining: true,
  isolating: true,

  addAttributes() {
    return {
      title: {
        default: '',
        parseHTML: (el: HTMLElement) => el.querySelector('.fk-step-title')?.textContent?.trim() || '',
        renderHTML: () => ({}),
      },
    };
  },

  parseHTML() {
    return [{ tag: 'li.fk-step', contentElement: '.fk-step-body' }];
  },

  renderHTML({ HTMLAttributes, node }) {
    return [
      'li',
      mergeAttributes(HTMLAttributes, { class: 'fk-step' }),
      ['div', { class: 'fk-step-title' }, String(node.attrs.title || '')],
      ['div', { class: 'fk-step-body' }, 0],
    ];
  },

  addNodeView() {
    return ReactNodeViewRenderer(StepView);
  },
});

function StepsView(props: NodeViewProps): JSX.Element {
  const { node, editor, getPos, deleteNode } = props;
  const addStep = (): void => {
    const pos = getPos();
    editor
      .chain()
      .focus()
      .insertContentAt(pos + node.nodeSize - 1, {
        type: 'fkStep',
        attrs: { title: `Шаг ${node.childCount + 1}` },
        content: [{ type: 'paragraph' }],
      })
      .run();
  };
  return (
    <NodeViewWrapper className="fk-layout-node">
      <div className="fk-layout-bar" contentEditable={false}>
        <span>Шаги: {node.childCount}</span>
        <button type="button" onClick={addStep} title="Добавить шаг"><PlusOutlined /></button>
        <button type="button" onClick={() => deleteNode()} title="Удалить блок" className="danger"><DeleteOutlined /></button>
      </div>
      <NodeViewContent as="ol" className="fk-steps" data-steps="" />
    </NodeViewWrapper>
  );
}

function StepView(props: NodeViewProps): JSX.Element {
  const { node, updateAttributes, deleteNode } = props;
  return (
    <NodeViewWrapper as="li" className="fk-step">
      <div className="fk-step-title-row" contentEditable={false}>
        <input
          value={String(node.attrs.title ?? '')}
          onChange={(e) => updateAttributes({ title: e.target.value })}
          placeholder="Название шага"
          className="fk-layout-input fk-step-title"
        />
        <button type="button" onClick={() => deleteNode()} title="Удалить шаг" className="fk-layout-btn danger"><DeleteOutlined /></button>
      </div>
      <NodeViewContent className="fk-step-body" />
    </NodeViewWrapper>
  );
}

/** Содержимое для slash-команд: пустые заготовки блоков. */
export const LAYOUT_TEMPLATES: Record<'columns' | 'toggle' | 'steps', JSONContent> = {
  columns: {
    type: 'fkColumns',
    content: [
      { type: 'fkColumn', content: [{ type: 'paragraph' }] },
      { type: 'fkColumn', content: [{ type: 'paragraph' }] },
    ],
  },
  toggle: { type: 'fkToggle', attrs: { title: 'Заголовок' }, content: [{ type: 'paragraph' }] },
  steps: {
    type: 'fkSteps',
    content: [
      { type: 'fkStep', attrs: { title: 'Шаг 1' }, content: [{ type: 'paragraph' }] },
      { type: 'fkStep', attrs: { title: 'Шаг 2' }, content: [{ type: 'paragraph' }] },
    ],
  },
};
